<script setup lang="ts">
import {computed, reactive, ref} from "vue"
import axios from "axios"

const SIGNUP_ENDPOINT = "/classwallet/signup/register"
const REDIRECT_TO = "/classwallet/shop"

const fieldErrors = reactive<Record<string, string[]>>({})
const isSubmitting = ref(false)
const error = ref("")
const success = ref(false)
const redirectUrl = ref("")

type DefaultFields = {
  first_name: string
  last_name: string
  email: string
}

const props = withDefaults(
  defineProps<{ defaultFields?: DefaultFields }>(),
  {defaultFields: () => ({first_name: "", last_name: "", email: ""})}
)

const form = reactive({
  first_name: props.defaultFields.first_name,
  last_name: props.defaultFields.last_name,
  email: props.defaultFields.email,
  login: "",
  user_type: "parent",
  grade_level: "",
  how_did_you_find_us: "",
  password: "",
  redirect_to: REDIRECT_TO
})

const isFormComplete = computed(() =>
  form.first_name && form.last_name && form.email && form.login && form.password && form.grade_level && form.how_did_you_find_us
)

const referralTypes = [
  "Pinterest",
  "A Friend Referred Me",
  "Search Engine",
  "Facebook",
  "Twitter",
  "Instagram",
  "Other",
  "Unknown",
]

async function submit() {
  // Clear any existing field errors before submission
  Object.keys(fieldErrors).forEach(key => delete fieldErrors[key])
  error.value = ""
  success.value = false

  try {
    isSubmitting.value = true
    const response = await axios.post(SIGNUP_ENDPOINT, form)
    console.log("RESPONSE", response)

    if (response.data.success) {
      success.value = true
      redirectUrl.value = response.data.redirect
      window.location.href = redirectUrl.value
    }
  } catch (e: any) {
    console.error("Error during signup:", e)

    if (e.response?.status === 422 && e.response?.data?.errors) {
      // Handle validation errors
      Object.entries(e.response.data.errors).forEach(([field, messages]) => {
        fieldErrors[field] = messages as string[]
      })
      console.warn("Validation errors:", fieldErrors)
    } else {
      // Handle other errors
      error.value = e.response?.data?.message || e.message || "An error occurred during signup. Please try again."
    }
  } finally {
    isSubmitting.value = false
  }
}

function isInvalid(field: string) {
  return fieldErrors[field]?.length > 0
}
</script>

<template>
  <div class="container mt-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header">
            <h2 class="text-center">Sign Up for Lyrics2Learn</h2>
          </div>
          <div class="card-body">
            <div v-if="error" class="alert alert-danger">
              <strong>Error:</strong>
              {{ error }}
            </div>

            <div v-if="success" class="alert alert-success">
              Signup successful! Redirecting...
            </div>

            <form v-else method="post" @submit.prevent="submit">
              <div class="form-group mb-3">
                <label for="first_name">First Name</label>
                <input
                  id="first_name"
                  v-model="form.first_name"
                  type="text"
                  class="form-control"
                  required
                  minlength="2"
                  maxlength="45"
                >
              </div>

              <div class="form-group mb-3">
                <label for="last_name">Last Name</label>
                <input
                  id="last_name"
                  v-model="form.last_name"
                  type="text"
                  class="form-control"
                  required
                  minlength="1"
                  maxlength="45"
                >
              </div>

              <div class="form-group mb-3">
                <label for="login">Username</label>
                <input
                  id="login"
                  v-model="form.login"
                  type="text"
                  class="form-control"
                  :class="{'is-invalid': isInvalid('login')}"
                  required
                  minlength="4"
                  maxlength="20"
                  autocomplete="off"
                  autocapitalize="none"
                >
                <div v-if="isInvalid('login')" class="invalid-feedback">
                  {{ fieldErrors.login[0] }}
                </div>
              </div>

              <div class="form-group mb-3">
                <label for="email">Email</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  :class="{'is-invalid': isInvalid('email')}"
                  required
                >
                <div v-if="isInvalid('email')" class="invalid-feedback">
                  {{ fieldErrors.email[0] }}
                </div>
              </div>

              <div class="form-group mb-3">
                <label for="password">Password</label>
                <input
                  id="password"
                  v-model="form.password"
                  type="password"
                  class="form-control"
                  :class="{'is-invalid': isInvalid('password')}"
                  required
                  minlength="6"
                  maxlength="32"
                >
                <div v-if="isInvalid('password')" class="invalid-feedback">
                  {{ fieldErrors.password[0] }}
                </div>
              </div>

              <div class="form-group mb-3">
                <label for="grade_level">Grade Level</label>
                <select
                  id="grade_level"
                  v-model="form.grade_level"
                  class="form-control"
                  :class="{'is-invalid': isInvalid('grade_level')}"
                  required
                >
                  <option value="">--Select grade level--</option>
                  <option value="Kindergarten">Kindergarten</option>
                  <option value="1st Grade">1st Grade</option>
                  <option value="2nd grade">2nd grade</option>
                  <option value="3rd grade">3rd grade</option>
                  <option value="4th grade">4th grade</option>
                  <option value="5th grade">5th grade</option>
                  <option value="6th grade or higher">6th grade or higher</option>
                  <option value="Multiple grade levels">Multiple grade levels</option>
                </select>
                <div v-if="isInvalid('grade_level')" class="invalid-feedback">
                  {{ fieldErrors.grade_level[0] }}
                </div>
              </div>

              <div class="form-group mb-3">
                <label for="how_did_you_find_us">How did you find us?</label>
                <select
                  v-model="form.how_did_you_find_us"
                  class="select_new form-control"
                  :class="{'is-invalid': isInvalid('how_did_you_find_us')}"
                  required
                >
                  <option value="">--Select a choice--</option>
                  <option v-for="(how, index) in referralTypes" :key="index" :value="how">{{ how }}</option>
                </select>
                <div v-if="isInvalid('how_did_you_find_us')" class="invalid-feedback">
                  {{ fieldErrors.how_did_you_find_us[0] }}
                </div>
              </div>


              <div class="form-group mb-4">
                <button
                  type="submit"
                  class="btn btn-primary btn-lg w-100"
                  :disabled="isSubmitting || !isFormComplete"
                >
                  <span v-if="isSubmitting">Signing up...</span>
                  <span v-else>Sign Up</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;
}

.btn-primary {
  background-color: #c41717;
  border-color: #c41717;

  &:hover, &:focus {
    background-color: #b01415;
    border-color: #b01415;
  }

  &:disabled {
    background-color: #aaa;
    border-color: #aaa;
  }
}
</style>
